import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import type { StayPlanUsage } from "@/hooks/useStayPlanContracts";
import { planUsageDetailKey, type PlanUsageDetail } from "@/hooks/usePlanUsageDetails";
import { buildUsageDetail, buildUsageHeadline } from "@/components/packages/plan-card/usageLabels";

export interface PlanUsageTimelineProps {
  usages: StayPlanUsage[];
  /** Total de diárias do ciclo ou do pacote, para o contador do cabeçalho. */
  total: number | null;
  /** Detalhe por diária. Vazio enquanto carrega. */
  details: Map<string, PlanUsageDetail>;
  onOpenUsage: (usage: StayPlanUsage) => void;
}

export function PlanUsageTimeline({ usages, total, details, onOpenUsage }: PlanUsageTimelineProps) {
  const { t, i18n } = useTranslation("patients");
  const locale = i18n.language.startsWith("pt") ? ptBR : enUS;
  // Uma diária reservada ainda vai acontecer; listá-la junto das utilizadas
  // fazia o card dizer que já foi usada.
  const [chosenTab, setTab] = useState<"upcoming" | "used" | null>(null);
  const upcoming = usages.filter((usage) => usage.source === "hold");
  const done = usages.filter((usage) => usage.source !== "hold");
  // Sem nada reservado, abrir em "Próximas" mostraria um card vazio à toa.
  const tab = chosenTab ?? (upcoming.length > 0 ? "upcoming" : "used");
  const visible = tab === "upcoming" ? upcoming : done;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <p className="font-display text-[0.9375rem] font-bold text-foreground">{t("planCard.usagesTitle")}</p>
        <span className="rounded-full bg-cream-100 px-2.5 py-0.5 text-xs font-semibold text-color-medium">
          {total === null
            ? t("planCard.usagesCount", { count: usages.length })
            : t("planCard.usagesOfTotal", { used: usages.length, total })}
        </span>
        {usages.length ? (
          <p className="ms-auto text-xs text-color-light">{t("planCard.usagesHint")}</p>
        ) : null}
      </div>

      <div role="tablist" aria-label={t("planCard.usagesTitle")} className="mt-2.5 flex gap-1.5">
        {(["upcoming", "used"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              tab === value ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:bg-muted"
            }`}
          >
            {t(`planCard.usagesTabs.${value}`)} ({value === "upcoming" ? upcoming.length : done.length})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="mt-3 rounded-lg border border-border bg-cream-50 px-4 py-4 text-sm text-muted-foreground">
          {t(tab === "upcoming" ? "planCard.usagesEmptyUpcoming" : "planCard.usagesEmptyUsed")}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {visible.map((usage) => {
            const day = parseISO(usage.usedOn);
            const detail = details.get(planUsageDetailKey(usage.stayReservationId, usage.usedOn));

            const headline = buildUsageHeadline(detail, {
              period: (key) => t(`planCard.period.${key}`, { defaultValue: key }),
              overnight: t("planCard.overnight"),
              day: t("planCard.usageDay"),
            });
            const line = buildUsageDetail(detail, {
              checkIn: (time) => t("planCard.checkInAt", { time }),
              checkOut: (time) => t("planCard.checkOutAt", { time }),
              pack: (name) => t("planCard.packNamed", { name }),
            });

            return (
              <li key={usage.id}>
                <button
                  type="button"
                  onClick={() => onOpenUsage(usage)}
                  aria-label={t("stayPlans.openReservation", { date: usage.usedOn })}
                  className="flex flex-col md:flex-row w-full items-center gap-3.5 rounded-lg border border-border bg-background px-4 py-3 text-start transition-shadow hover:border-foreground/20 hover:shadow-akuna-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex shrink-0 flex-col items-center leading-none">
                    <span className="font-display text-[1.0625rem] font-extrabold text-foreground">
                      {format(day, "dd", { locale })}
                    </span>
                    <span className="mt-1 text-[0.6875rem] uppercase tracking-wide text-color-light">
                      {format(day, "MMM", { locale })} · {format(day, "EEEEEE", { locale })}
                    </span>
                  </span>

                  <span className="h-8 w-px shrink-0 bg-foreground/10" aria-hidden />

                  <span className="min-w-0">
                    <span className="block text-[0.8125rem] font-semibold text-foreground">{headline}</span>
                    {line ? <span className="block text-xs text-muted-foreground">{line}</span> : null}
                    {usage.units > 1 ? (
                      <span className="block text-xs text-muted-foreground">
                        {t("daycarePlans.usageUnits", { units: usage.units })}
                      </span>
                    ) : null}
                  </span>

                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                      usage.source === "hold"
                        ? "bg-muted text-muted-foreground"
                        : "bg-cream-100 text-color-medium"
                    }`}
                  >
                    {usage.source === "hold" ? t("planCard.usageHold") : t("planCard.usageCheckin")}
                  </span>

                  <ChevronRight className="hidden md:block ms-auto h-[18px] w-[18px] shrink-0 text-color-light" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
